/**
 * Compute the [05:00 startDate, 04:59 endDate+1] window bounds and the fetchEndDate.
 * We fetch through endPlusOne (YYYY-MM-DD) and post-filter by 05:00 window.
 */
export function computeDateWindow(startDate, endDate) {
  // Lower bound: 05:00 on startDate
  const lowerBound = new Date(`${startDate}T05:00:00`);

  // Upper bound: 05:00 on the day *after* endDate
  const endPlusOne = new Date(endDate);
  endPlusOne.setDate(endPlusOne.getDate() + 1);
  const endPlusOneStr = endPlusOne.toISOString().split("T")[0];
  const upperBound = new Date(`${endPlusOneStr}T05:00:00`);

  return { lowerBound, upperBound, fetchEndDate: endPlusOneStr };
}

/**
 * Fetch + filter service reports using the same behavior as ManageServiceReports.
 *
 * @param {Object} params
 * @param {Object} params.searchFilter - { client, startDate, endDate, endorsementForm }
 * @param {string[]} params.userRoles  - current user's roles
 * @param {string|null} params.technicianId - current user's technician_id (if any)
 * @param {Function} params.getJobOrdersApi - API function getJobOrders(status, serviceReportStatus, billingRecordStatus, startDate, endDate, clientId, technicianId?)
 *
 * @returns {Promise<{ list: Array, total: number }>}
 */
export async function fetchAndFilterJobOrders({
  searchFilter,
  status = [],                // e.g. ["Done"], ["Not Done"], or []
  serviceReportStatus = [],   // e.g. ["Done"] for service-report screens
  billingRecordStatus = [],   // if you need it
  userRoles = [],
  technicianId = null,
  getJobOrdersApi,
}) {
  if (typeof getJobOrdersApi !== "function") {
    throw new Error("getJobOrdersApi is required");
  }

  const clientId = searchFilter?.client?.id || null;
  const startDate = searchFilter?.startDate || null;
  const endDate = searchFilter?.endDate || null;

  // 1) Compute window & fetchEndDate (endDate + 1 day)
  const { lowerBound, upperBound, fetchEndDate } = computeDateWindow(
    startDate,
    endDate
  );

  // 2) Fetch from API using parameters passed in
  //    Follow your getJobOrders signature: (status, serviceReportStatus, billingRecordStatus, startDate, endDate, clientId)
  const jobOrderResults =
    (await getJobOrdersApi(status, serviceReportStatus, billingRecordStatus, startDate, fetchEndDate, clientId)) || [];

  // 3) Filter by the 05:00 → next-day 04:59 window across the full range
  const windowFiltered = jobOrderResults.filter((job) => {
    const dt = new Date(`${job.job_date}T${job.start_time}:00`);
    return dt >= lowerBound && dt < upperBound;
  });

  // 4) Filter by endorsement form (Yes/No/All)
  const endorsementFiltered = windowFiltered.filter((job) => {
    if (searchFilter?.endorsementForm === "Yes") return !!job.endorsement_form_id;
    if (searchFilter?.endorsementForm === "No") return !job.endorsement_form_id;
    return true;
  });

  // 5) Sort by job_date + start_time (ascending)
  const sorted = endorsementFiltered.sort((a, b) => {
    const tA = new Date(`${a.job_date}T${a.start_time}:00`).getTime();
    const tB = new Date(`${b.job_date}T${b.start_time}:00`).getTime();
    return tA - tB;
  });

  // 6) Shape each record (client name + safe job_types array)
  const shaped = sorted.map((job) => ({
    ...job,
    client: job?.client?.name || "",
    job_types: Array.isArray(job?.job_types) ? job.job_types : [],
    technicians: Array.isArray(job?.technicians) ? job.technicians : [],
  }));

  // 7) Technician scoping: if user is strictly an operations_technician
  let finalResults = shaped;
  // Only scope if BOTH: (a) role includes operations_technician, AND (b) you provided a technicianId
  if (userRoles.includes("operations_technician") && technicianId) {
    finalResults = shaped.filter((job) =>
      job.technicians.some((t) => t?.id === technicianId)
    );
  }

  return {
    list: finalResults,
    total: finalResults.length,
  };
}

/**
 * Single-day fetch & filter for Service Report creation screens.
 * - Uses 05:00 → next-day 04:59 window
 * - Defaults to serviceReportStatus = ["Not Done"]
 * - Preserves client object shape; only normalizes job_types array
 */
export async function fetchAndFilterJOsBySingleDate({
  date,                    // "YYYY-MM-DD"
  userRoles = [],
  technicianId = null,
  getJobOrdersApi,         // (status, serviceReportStatus, billingRecordStatus, startDate, endDate, clientId?)
  serviceReportStatus = ["Not Done"],
}) {
  if (!date) return { list: [], total: 0 };
  if (typeof getJobOrdersApi !== "function") {
    throw new Error("getJobOrdersApi is required");
  }

  // Build 05:00 window for the day
  const startDate = date;
  const lowerBound = new Date(`${startDate}T05:00:00`);
  const nextDay = new Date(startDate);
  nextDay.setDate(nextDay.getDate() + 1);
  const nextDateStr = nextDay.toISOString().split("T")[0];
  const upperBound = new Date(`${nextDateStr}T05:00:00`);

  // Fetch through next day (so we can post-filter by the window)
  const jobOrderResults =
    (await getJobOrdersApi([], serviceReportStatus, [], startDate, nextDateStr, null)) || [];

  // Apply 05:00 → next-day 04:59 window
  const windowFiltered = jobOrderResults.filter((record) => {
    const dt = new Date(`${record.job_date}T${record.start_time}:00`);
    return dt >= lowerBound && dt < upperBound;
  });

  // Sort ascending by schedule
  const sorted = windowFiltered.sort((a, b) => {
    const tA = new Date(`${a.job_date}T${a.start_time}:00`).getTime();
    const tB = new Date(`${b.job_date}T${b.start_time}:00`).getTime();
    return tA - tB;
  });

  // Normalize job_types only (keep client object shape intact)
  const shaped = sorted.map((job) => ({
    ...job,
    job_types: Array.isArray(job.job_types) ? job.job_types : [],
  }));

  // Technician scoping
  let finalResults = shaped;
  if (userRoles.includes("operations_technician")) {
    if (technicianId) {
      finalResults = shaped.filter((job) =>
        job.technicians?.some((t) => t?.id === technicianId)
      );
    } else {
      finalResults = [];
    }
  }

  return { list: finalResults, total: finalResults.length };
}
